// Sprint notification-center (spec D2) — Web Push subscription helpers.
//
// iOS ≥16.4 supports Web Push ONLY for installed PWAs and ONLY after a
// user-gesture-triggered Notification.requestPermission(). The Settings
// page owns that gesture ("Enable on this device"); this module owns
// the mechanics: fetch the VAPID public key, subscribe via the active
// service-worker registration, sync with the backend.
//
// New (verified no equivalent exists).

import { api } from './api';

export type PushSupport =
  | 'unsupported' // browser has no push API (or no SW registration possible)
  | 'denied' // user permanently denied notifications for this origin
  | 'ready'; // supported; may or may not be subscribed yet

export function pushSupport(): PushSupport {
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  return 'ready';
}

// The Push API wants the VAPID public key as a Uint8Array.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch {
    return null;
  }
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() === 'unsupported') return null;
  const reg = await registration();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Sprint web-push-vapid-activation (spec D4) — every failure branch names
// its reason so the Settings page can show per-reason founder copy instead
// of a silent no-op button.
export type PushFailureReason =
  | 'server_disabled' // backend has no VAPID keys yet (public-key endpoint says disabled)
  | 'permission_denied' // user denied the notification permission prompt
  | 'no_registration' // no active service-worker registration (not installed as PWA?)
  | 'error'; // anything else (subscribe threw, incomplete keys, network)

export type SubscribeResult = { ok: true } | { ok: false; reason: PushFailureReason };

// Must be called from a user gesture (iOS requirement). Never throws —
// failures come back as `{ ok: false, reason }` so the caller shows the
// founder-mode failure copy, not an exception.
export async function subscribeToPush(): Promise<SubscribeResult> {
  try {
    const support = pushSupport();
    if (support === 'denied') return { ok: false, reason: 'permission_denied' };
    if (support !== 'ready') return { ok: false, reason: 'error' };
    const { enabled, publicKey } = await api<{ enabled: boolean; publicKey: string }>(
      '/api/push/public-key',
    );
    if (!enabled || !publicKey) return { ok: false, reason: 'server_disabled' };
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: 'permission_denied' };
    const reg = await registration();
    if (!reg) return { ok: false, reason: 'no_registration' };
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
    });
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return { ok: false, reason: 'error' };
    }
    await api('/api/push/subscriptions', {
      method: 'POST',
      body: {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      },
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

// Sprint test-notification-button (spec D1) — fire a real Tier A test
// notification through the production path (feed + WS + toast + Web Push),
// scoped server-side to the caller's own devices. No body: the target is
// the session user, resolved on the server (spec D2). Throws ApiHttpError
// on failure so the caller can branch on the code (`RATE_LIMITED` → the
// "wait a few seconds" copy, spec D4).
export async function sendTestNotification(): Promise<void> {
  await api('/api/push/test', { method: 'POST' });
}

export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const sub = await getCurrentSubscription();
    if (!sub) return true;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    await api('/api/push/subscriptions', { method: 'DELETE', body: { endpoint } });
    return true;
  } catch {
    return false;
  }
}
